/**
 * The single copy source for everything the surfaces say about an ad's targeting.
 *
 * Meta discloses targeting for the EU and the UK only, so an ad with no rows is almost never an
 * ad that targets nowhere — it is an ad we have not collected, or one Meta never published. Every
 * state here is therefore framed as collection, never as absence: no surface says the data does
 * not exist. Change `add-meta-ad-library-country-probe` extends the same helper with the probe
 * states rather than adding a second vocabulary.
 */

/** Mirrors the API's `AdLocationSource`; `COUNTRY_PROBE` is added by the country-probe change. */
export type ITargetingBasis = 'EU_TRANSPARENCY' | 'UK_TRANSPARENCY' | 'COUNTRY_PROBE' | null

/** The sentence every non-EU surface shows in place of an empty state. */
export const NON_EU_TARGETING_DISCLOSURE =
    'Targeting details are disclosed by Meta only for ads delivered in the EU/UK.'

const BASIS_CAPTION: Record<'EU_TRANSPARENCY' | 'UK_TRANSPARENCY', string> = {
    EU_TRANSPARENCY: 'EU transparency data',
    UK_TRANSPARENCY: 'UK transparency data',
}

/** What the country probe found, or why it has not looked. Mirrors the API's own four states. */
export type IAdCountryProbe = {
    state: 'OBSERVED' | 'NOT_OBSERVED' | 'PENDING' | 'UNAVAILABLE'
    observedCountryCodes: string[]
    probedAt: string | null
}

/**
 * The card's targeting row. Keyed on the basis so a new basis brings its own label instead of
 * inheriting a hard-coded `Targets`.
 */
export function getTargetingRowCopy(basis: ITargetingBasis) {
    switch (basis) {
        case 'EU_TRANSPARENCY':
        case 'UK_TRANSPARENCY':
            return {
                label: 'Targets',
                caption: BASIS_CAPTION[basis],
                tooltip: `Countries this ad targets, as disclosed by Meta (${BASIS_CAPTION[basis]}).`,
            }
        case 'COUNTRY_PROBE':
            return {
                label: 'Seen in',
                caption: 'Observed in the Meta Ad Library',
                tooltip: 'Countries whose Meta Ad Library returned this ad. Meta discloses no targeting for it.',
            }
        default:
            return null
    }
}

/**
 * The Details tab's neutral row when an ad has no targeting rows. Never a negative statement:
 * "not collected" is the truth, "does not exist" is not.
 */
export function getMissingTargetingCopy({
    isEuRegion,
    detailFetched,
    isActive,
}: {
    isEuRegion: boolean
    detailFetched: boolean
    isActive: boolean
}) {
    if (!isEuRegion) {
        return { text: NON_EU_TARGETING_DISCLOSURE, tooltip: null }
    }

    if (detailFetched) {
        // Fetched and still nothing: Meta published no transparency block for this ad.
        return {
            text: 'Targeting data could not be collected for this ad',
            tooltip: 'Meta published no transparency block for this ad when it was fetched.',
        }
    }

    if (isActive) {
        return {
            text: 'Targeting data not collected yet',
            tooltip:
                'Meta discloses targeting for ads delivered in the EU/UK; this ad has not been fetched in detail yet.',
        }
    }

    // The window has closed: an ended ad will never be fetched in detail again.
    return {
        text: 'Targeting data was not collected while this ad was live',
        tooltip: 'Meta discloses targeting for ads delivered in the EU/UK; this ad ended before it was fetched.',
    }
}

/**
 * Country name for an ISO2 code, falling back to the code itself.
 *
 * `fallback: 'none'` is deliberate: the default hands back "Unknown Region" for an unassigned
 * code, which reads as a real place. The raw code is the honest answer.
 */
export function formatCountryName(code: string, locale = 'en') {
    try {
        return new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }).of(code) ?? code
    } catch {
        return code
    }
}

/**
 * Grouped by granularity, coarse to fine. Every member of `AdLocationType` needs an entry:
 * `groupTargetingRows` maps over this list, so a type missing from it is dropped without trace.
 * That is not hypothetical — a postal-only ad's aggregate row is the ONLY geography it has, and
 * an unlisted type would show such an ad as targeting nowhere.
 */
export const TARGETING_GROUPS = [
    { type: 'COUNTRY', label: 'Countries' },
    { type: 'REGION', label: 'Regions' },
    { type: 'COUNTY', label: 'Counties' },
    { type: 'CITY', label: 'Cities' },
    { type: 'POSTAL_AGGREGATE', label: 'Postal areas' },
    { type: 'COUNTRY_GROUP', label: 'Groups' },
    { type: 'UNKNOWN', label: 'Other' },
] as const

export type ITargetingLocationRow = {
    kind: string
    type: string
    name: string
    countryCode: string | null
    excluded: boolean
    /** POSTAL_AGGREGATE rows only: how many disclosed places the one row stands in for. */
    aggregateCount?: number | null
}

/**
 * Countries carry an ISO2 code worth resolving; every other granularity is already a place name.
 *
 * An aggregate says how many places it replaced — "Germany (postal codes)" alone would read as a
 * granularity the advertiser chose rather than one we folded, and the count is the information.
 */
export function targetingRowName(
    row: Pick<ITargetingLocationRow, 'name' | 'type' | 'countryCode' | 'aggregateCount'>
) {
    if (row.type === 'COUNTRY' && row.countryCode) return formatCountryName(row.countryCode)
    if (row.type === 'POSTAL_AGGREGATE' && row.aggregateCount) {
        return row.name.replace(/\(([^)]+)\)$/, `(${row.aggregateCount.toLocaleString('en-US')} $1)`)
    }
    return row.name
}

/**
 * How many place names one granularity shows before the rest collapse into a count.
 *
 * A locally targeted ad can disclose thousands of places, and every excluded one renders as its
 * own chip — unbounded, a single ad put well over two thousand elements in this panel. Twelve is
 * enough to read the shape of a targeting setup; past that the count is the information.
 */
export const MAX_VISIBLE_TARGETING_NAMES = 12

/** Names to show plus how many were left off, never the whole list. */
function capNames(names: string[]) {
    return { names: names.slice(0, MAX_VISIBLE_TARGETING_NAMES), hidden: Math.max(0, names.length - MAX_VISIBLE_TARGETING_NAMES) }
}

/**
 * The Details tab's rows: one per present granularity, included names and excluded names kept
 * apart so "Worldwide minus two countries" reads as what it is.
 *
 * `REACH` rows are dropped — they say where an ad was delivered, not where it was aimed, and an
 * EU ad carries one per country of its breakdown. An empty group renders nothing; no kept row is
 * silently omitted, which is why `UNKNOWN` has a group of its own.
 *
 * Each side is capped: the group reports what it shows AND how many it withheld, so a truncated
 * list is never mistaken for a complete one.
 */
export function groupTargetingRows(locations: ITargetingLocationRow[]) {
    const targeting = locations.filter((row) => row.kind === 'TARGETING')
    // A type this list does not name falls through to `Other` rather than vanishing. The list is
    // maintained here while the enum lives in Prisma, so the two WILL drift — and a dropped row
    // is invisible: a postal-only ad would render as targeting nowhere, which is precisely the
    // geography this change exists to surface.
    const known = new Set<string>(TARGETING_GROUPS.map(({ type }) => type))
    const groupOf = (row: ITargetingLocationRow) => (known.has(row.type) ? row.type : 'UNKNOWN')

    return TARGETING_GROUPS.map(({ type, label }) => {
        const rows = targeting.filter((row) => groupOf(row) === type)
        const included = rows.filter((row) => !row.excluded).map(targetingRowName)
        const excluded = rows.filter((row) => row.excluded).map(targetingRowName)
        return { label, total: included.length + excluded.length, included: capNames(included), excluded: capNames(excluded) }
    }).filter((group) => group.total > 0)
}

/** How many countries fit on the card before the row truncates. */
export const MAX_VISIBLE_TARGET_COUNTRIES = 3

/**
 * The card's country line for an ad that discloses targeting AND was observed abroad.
 *
 * The two are never merged into one list: "targets DE, AT" and "was also seen in AU" are
 * different claims, and a reader cannot split a merged list back apart.
 */
export function formatTargetsWithObservedLabel(
    targetCodes: string[],
    probe: IAdCountryProbe | null | undefined
) {
    const targets = formatTargetCountriesLabel(targetCodes)
    if (!targets) return null
    if (!probe || probe.state !== 'OBSERVED' || probe.observedCountryCodes.length === 0) return targets

    const observed = probe.observedCountryCodes.map((code) => formatCountryName(code)).join(', ')
    return {
        short: `${targets.short} · also seen in ${observed}`,
        full: `${targets.full} · also seen in ${observed}`,
    }
}

/** The card's country list: the first few names plus how many were left off. */
export function formatTargetCountriesLabel(codes: string[]) {
    if (codes.length === 0) return null
    const names = codes.map((code) => formatCountryName(code))
    const visible = names.slice(0, MAX_VISIBLE_TARGET_COUNTRIES)
    const overflow = names.length - visible.length
    return {
        short: overflow > 0 ? `${visible.join(', ')} +${overflow}` : visible.join(', '),
        full: names.join(', '),
    }
}


/**
 * What the modal says about where a non-EU ad was actually seen running.
 *
 * Every state is framed as looking, never as fact about the world: a probe that found nothing
 * means we did not see the ad in the countries we can ask, not that it runs nowhere.
 */
export function getCountriesObservedCopy(probe: IAdCountryProbe | null | undefined) {
    if (!probe) return null

    const probedOn = probe.probedAt ? new Date(probe.probedAt).toLocaleDateString() : null

    switch (probe.state) {
        case 'OBSERVED':
            return {
                value: probe.observedCountryCodes.map((code) => formatCountryName(code)).join(', '),
                caption: probedOn ? `Checked ${probedOn}` : null,
                tooltip:
                    'Countries whose Meta Ad Library returned this ad when we checked. Meta discloses no targeting outside the EU/UK.',
            }
        case 'NOT_OBSERVED':
            return {
                value: 'Not seen in the countries we check',
                caption: probedOn ? `Checked ${probedOn}` : null,
                tooltip: 'We asked each supported country´s ad library and none returned this ad.',
            }
        case 'PENDING':
            return {
                value: 'Not checked yet',
                caption: null,
                tooltip: 'This advertiser has not been checked country by country yet.',
            }
        case 'UNAVAILABLE':
            return {
                value: 'Not checked for this advertiser',
                caption: null,
                tooltip:
                    'Checking runs country by country, so it is reserved for advertisers with a small number of live ads.',
            }
    }
}

/** The audience panel's second line, for a non-EU ad we did see somewhere. */
export function getObservedDeliveryLine(probe: IAdCountryProbe | null | undefined) {
    if (!probe || probe.state !== 'OBSERVED' || probe.observedCountryCodes.length === 0) return null

    const names = probe.observedCountryCodes.map((code) => formatCountryName(code)).join(', ')
    const probedOn = probe.probedAt ? new Date(probe.probedAt).toLocaleDateString() : null
    return probedOn ? `Observed delivering in ${names} · checked ${probedOn}` : `Observed delivering in ${names}`
}
